import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  FormControlLabel,
  Switch,
  Stack,
  RadioGroup,
  Radio,
  Alert,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import LinkIcon from '@mui/icons-material/Link';
import VideocamIcon from '@mui/icons-material/Videocam';
import axios from '../../api/axios';
import ImageUploader from './ImageUploader';
import FileUploader from './FileUploader';
import VideoUploader from './VideoUploader';

const ConsultationsManager = () => {
  const [consultations, setConsultations] = useState([]);
  const [open, setOpen] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState({
    name: '',
    key: '',
    order: 0,
  });
  const [formData, setFormData] = useState({
    title: '',
    category: 'general',
    categoryName: 'Общие темы',
    externalUrl: '',
    fileUrl: '',
    mediaType: 'document',
    videoUrl: '',
    imageUrl: '',
    description: '',
    order: 0,
    published: true,
  });

  useEffect(() => {
    fetchConsultations();
    fetchCategories();
  }, []);

  const fetchConsultations = async () => {
    try {
      const response = await axios.get('/consultations');
      setConsultations(response.data);
    } catch (error) {
      console.error('Ошибка при загрузке консультаций:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get('/consultation-categories');
      const normalized = response.data.map((category) => ({
        value: category.key,
        label: category.name,
        order: category.order ?? 0,
      }));
      setCategories(normalized);
      if (normalized.length > 0) {
        setFormData((prev) => {
          if (prev.category && normalized.some((item) => item.value === prev.category)) {
            return prev;
          }
          return {
            ...prev,
            category: normalized[0].value,
            categoryName: normalized[0].label,
          };
        });
      }
    } catch (error) {
      console.error('Ошибка при загрузке категорий консультаций:', error);
    }
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingId(null);
    setFormData({
      title: '',
      category: categories[0]?.value || '',
      categoryName: categories[0]?.label || '',
      externalUrl: '',
      fileUrl: '',
      mediaType: 'document',
      videoUrl: '',
      imageUrl: '',
      description: '',
      order: 0,
      published: true,
    });
  };

  const handleEdit = (consultation) => {
    setEditingId(consultation._id);
    setFormData({
      title: consultation.title,
      category: consultation.category,
      categoryName: consultation.categoryName,
      externalUrl: consultation.externalUrl || '',
      fileUrl: consultation.fileUrl || '',
      mediaType: consultation.mediaType || 'document',
      videoUrl: consultation.videoUrl || '',
      imageUrl: consultation.imageUrl || '',
      description: consultation.description || '',
      order: consultation.order || 0,
      published: consultation.published,
    });
    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Вы уверены, что хотите удалить эту консультацию?')) {
      try {
        await axios.delete(`/consultations/${id}`);
        fetchConsultations();
      } catch (error) {
        console.error('Ошибка при удалении консультации:', error);
      }
    }
  };

  const handleSubmit = async () => {
    if (!formData.category) {
      alert('Сначала добавьте или выберите категорию консультации');
      return;
    }

    if (formData.mediaType === 'video') {
      if (!formData.videoUrl) {
        alert('Пожалуйста, загрузите видео или укажите ссылку на видео');
        return;
      }
    } else if (!formData.fileUrl && !formData.externalUrl) {
      alert('Пожалуйста, либо загрузите файл, либо укажите внешнюю ссылку');
      return;
    }

    try {
      const dataToSend = {
        ...formData,
        externalUrl: formData.mediaType === 'video'
          ? formData.videoUrl
          : (formData.fileUrl || formData.externalUrl),
      };
      
      if (editingId) {
        await axios.put(`/consultations/${editingId}`, dataToSend);
      } else {
        await axios.post('/consultations', dataToSend);
      }
      handleClose();
      fetchConsultations();
    } catch (error) {
      console.error('Ошибка при сохранении консультации:', error);
      alert('Ошибка при сохранении консультации: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleCategoryChange = (e) => {
    const selectedCategory = categories.find(cat => cat.value === e.target.value);
    setFormData({
      ...formData,
      category: e.target.value,
      categoryName: selectedCategory ? selectedCategory.label : '',
    });
  };

  const handleOpenCreateCategory = () => {
    setNewCategory({ name: '', key: '', order: categories.length * 10 + 10 });
    setCategoryDialogOpen(true);
  };

  const handleCreateCategory = async () => {
    if (!newCategory.name.trim()) {
      alert('Введите название категории');
      return;
    }

    try {
      const response = await axios.post('/consultation-categories', {
        name: newCategory.name.trim(),
        key: newCategory.key.trim() || undefined,
        order: Number(newCategory.order) || 0,
      });

      const created = { value: response.data.key, label: response.data.name };
      await fetchCategories();
      setFormData((prev) => ({
        ...prev,
        category: created.value,
        categoryName: created.label,
      }));
      setCategoryDialogOpen(false);
      setNewCategory({ name: '', key: '', order: 0 });
    } catch (error) {
      console.error('Ошибка при создании категории:', error);
      alert(error.response?.data?.message || 'Не удалось создать категорию');
    }
  };

  const getCategoryLabel = (category) => {
    const cat = categories.find(c => c.value === category);
    return cat ? cat.label : category;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Управление консультациями
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleClickOpen}
        >
          Добавить консультацию
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Превью</TableCell>
              <TableCell>Название</TableCell>
              <TableCell>Категория</TableCell>
              <TableCell>Тип</TableCell>
              <TableCell>Ссылка</TableCell>
              <TableCell>Статус</TableCell>
              <TableCell>Порядок</TableCell>
              <TableCell>Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {consultations.map((consultation) => (
              <TableRow key={consultation._id}>
                <TableCell>
                  {consultation.imageUrl ? (
                    <Box
                      component="img"
                      src={consultation.imageUrl}
                      alt={consultation.title}
                      sx={{
                        width: 60,
                        height: 45,
                        objectFit: 'cover',
                        borderRadius: 1,
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: 60,
                        height: 45,
                        backgroundColor: '#f0f0f0',
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Нет
                      </Typography>
                    </Box>
                  )}
                </TableCell>
                <TableCell>{consultation.title}</TableCell>
                <TableCell>
                  <Chip 
                    label={getCategoryLabel(consultation.category)} 
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  {(consultation.mediaType || 'document') === 'video' ? (
                    <Chip label="Видео" size="small" color="secondary" icon={<VideocamIcon />} />
                  ) : (
                    <Chip label="Документ" size="small" variant="outlined" />
                  )}
                </TableCell>
                <TableCell>
                  <a 
                    href={
                      consultation.mediaType === 'video'
                        ? consultation.videoUrl
                        : (consultation.fileUrl || consultation.externalUrl)
                    } 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: '#00ACC1' }}
                  >
                    <LinkIcon sx={{ mr: 0.5, fontSize: 18 }} />
                    <Typography variant="body2">
                      {(consultation.mediaType || 'document') === 'video' ? 'Открыть видео' : (consultation.fileUrl ? 'Открыть файл' : 'Открыть ссылку')}
                    </Typography>
                  </a>
                </TableCell>
                <TableCell>
                  {consultation.published ? (
                    <Chip label="Опубликовано" size="small" color="success" />
                  ) : (
                    <Chip label="Черновик" size="small" color="default" />
                  )}
                </TableCell>
                <TableCell>{consultation.order}</TableCell>
                <TableCell>
                  <IconButton onClick={() => handleEdit(consultation)} color="primary">
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(consultation._id)} color="error">
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingId ? 'Редактировать консультацию' : 'Добавить консультацию'}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Название консультации"
            name="title"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            fullWidth
            margin="normal"
            required
          />

          <FormControl fullWidth margin="normal">
            <InputLabel>Категория специалиста</InputLabel>
            <Select
              value={formData.category}
              onChange={handleCategoryChange}
              label="Категория специалиста"
            >
              {categories.map((category) => (
                <MenuItem key={category.value} value={category.value}>
                  {category.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Stack direction="row" spacing={1} sx={{ mt: 1, mb: 2 }}>
            <Button size="small" variant="outlined" onClick={handleOpenCreateCategory}>
              Добавить категорию
            </Button>
          </Stack>

          <Box sx={{ my: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Тип материала
            </Typography>
            <RadioGroup
              row
              value={formData.mediaType}
              onChange={(e) => setFormData({
                ...formData,
                mediaType: e.target.value,
                fileUrl: '',
                externalUrl: '',
                videoUrl: '',
              })}
            >
              <FormControlLabel value="document" control={<Radio />} label="Документ (PDF)" />
              <FormControlLabel value="video" control={<Radio />} label="Видео" />
            </RadioGroup>
          </Box>

          {formData.mediaType === 'document' ? (
          <Box sx={{ my: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Документ (PDF) - загрузить файл или указать ссылку
            </Typography>
            <FileUploader
              currentFile={formData.fileUrl}
              onUploadSuccess={(url) => {
                setFormData({ ...formData, fileUrl: url, externalUrl: '' });
              }}
              fileType="pdf"
              maxSizeMB={100}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
              ИЛИ укажите внешнюю ссылку:
            </Typography>
            <TextField
              label="Внешняя ссылка (URL)"
              name="externalUrl"
              value={formData.externalUrl}
              onChange={(e) => {
                setFormData({ ...formData, externalUrl: e.target.value, fileUrl: '' });
              }}
              fullWidth
              margin="normal"
              helperText="Например: https://example.com/consultation.pdf или любая другая ссылка"
              placeholder="https://"
            />
            {!formData.fileUrl && !formData.externalUrl && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                * Необходимо либо загрузить файл, либо указать внешнюю ссылку
              </Typography>
            )}
          </Box>
          ) : (
          <Box sx={{ my: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Видео - загрузить файл или указать ссылку
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              Поддерживается загрузка видеофайла, ссылки на YouTube, Vimeo и Яндекс.Диск
            </Alert>
            <VideoUploader
              currentVideo={formData.videoUrl}
              videoUrl={formData.videoUrl}
              onUploadSuccess={(url) => setFormData({ ...formData, videoUrl: url, externalUrl: url })}
              onUrlChange={(url) => setFormData({ ...formData, videoUrl: url, externalUrl: url })}
              inputId="consultation-video-upload-input"
              maxSizeMB={1536}
            />
            {!formData.videoUrl && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                * Необходимо загрузить видео или указать ссылку
              </Typography>
            )}
          </Box>
          )}

          <Box sx={{ my: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Изображение-превью (опционально)
            </Typography>
            <ImageUploader
              currentImage={formData.imageUrl}
              onUploadSuccess={(url) => setFormData({ ...formData, imageUrl: url })}
            />
            {formData.imageUrl && (
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={() => setFormData({ ...formData, imageUrl: '' })}
                sx={{ mt: 1 }}
              >
                Удалить изображение
              </Button>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Рекомендуемый размер: 400x300px. Будет отображаться на странице консультаций.
            </Typography>
          </Box>

          <TextField
            label="Описание (опционально)"
            name="description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            fullWidth
            margin="normal"
            multiline
            rows={3}
          />

          <TextField
            label="Порядок сортировки"
            name="order"
            type="number"
            value={formData.order}
            onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
            fullWidth
            margin="normal"
            helperText="Меньшее число = выше в списке"
          />

          <FormControlLabel
            control={
              <Switch
                checked={formData.published}
                onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
              />
            }
            label="Опубликовано"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Отмена</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingId ? 'Обновить' : 'Создать'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={categoryDialogOpen} onClose={() => setCategoryDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Новая категория консультаций</DialogTitle>
        <DialogContent>
          <TextField
            label="Название категории"
            value={newCategory.name}
            onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            label="Ключ категории (опционально)"
            value={newCategory.key}
            onChange={(e) => setNewCategory({ ...newCategory, key: e.target.value })}
            fullWidth
            margin="normal"
            helperText="Если не указывать, ключ будет сгенерирован автоматически из названия"
          />
          <TextField
            label="Порядок"
            type="number"
            value={newCategory.order}
            onChange={(e) => setNewCategory({ ...newCategory, order: parseInt(e.target.value, 10) || 0 })}
            fullWidth
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCategoryDialogOpen(false)}>Отмена</Button>
          <Button onClick={handleCreateCategory} variant="contained">
            Создать категорию
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConsultationsManager;
